import { useEffect, useRef, useState } from "react";

import styled from "styled-components";
import { PREMIUM_PADDING } from "theme/interpolations/padding";

import FnrCard from "UI/FnrCard";
import FnrFullPageColor from "UI/FnrFullPageColor";
import FnrLuke15 from "UI/FnrLuke15";
import FnrVirtueSignal from "UI/FnrVirtueSignal/FnrVirtueSignal";
import FnrHorizontalGap from "UI/commonContainers/FnrHorizontalGap";

import { colorIsLight } from "Utilities/Colors/colorUtils";
import useIsTallerThan from "Utilities/Hooks/useIsTallerThan";
import useIsUnloading from "Utilities/Hooks/useIsUnloading";
import useIsWiderThan from "Utilities/Hooks/useIsWiderThan";

import TwoFA from "./TwoFA";
import { usePostTwoFactorAuth } from "./TwoFA/API/queries";
import { use2FAState } from "./TwoFA/API/queryHooks";
import { variants } from "./TwoFA/constants";

const loginWidth = 440;

const translate = ({ $centered, $alignedTop }) => {
  if ($alignedTop && $centered) return "translate(-50%, 0);";
  if ($alignedTop) return "translate(0, 0);";
  if ($centered) return "translate(-50%, -50%);";
  return "translate(0, -50%);";
};

const PositionDiv = styled("div")`
  position: absolute;
  margin-left: ${({ $centered }) => ($centered ? "0px" : "12%")};
  left: ${({ $centered }) => ($centered ? "50%" : 0)};
  top: ${({ $alignedTop }) => ($alignedTop ? "10px" : "50%")};
  transform: ${translate};
  transition: margin-left 300ms, transform 300ms, left 300ms;
  overflow: hidden;
  border-radius: 20px;
  ${({ $showShadow }) =>
    $showShadow ? `box-shadow: rgba(0, 0, 0, 0.3) 0 4px 12px;` : ""}
  z-index: 1; // virtue icons to behind
`;

const Luke15PositionDiv = styled("div")`
  position: absolute;
  bottom: ${({ $onOneLine }) => ($onOneLine ? "28px" : "min(100px, 6%)")};
  right: ${({ $onOneLine }) => ($onOneLine ? "28px" : "min(100px, 6%)")};
  width: ${({ $onOneLine }) => ($onOneLine ? "100%" : "auto")};
  transition: margin-left 300ms, transform 300ms, bottom 300ms;
`;

const Login = () => {
  const networkShortname = window.location?.host?.split(".")[0];
  const [{ colorScheme = window.data.colorScheme }] = use2FAState();
  const { isFetched, data: { firstFactorSuccess } = {} } = usePostTwoFactorAuth(
    { variant: variants.login }
  );
  const [successSignal, setSuccessSignal] = useState(false);

  useEffect(() => {
    if (firstFactorSuccess) {
      setSuccessSignal(true);
    }
    setTimeout(() => setSuccessSignal(false), 1000);
  }, [firstFactorSuccess]);

  const containerRef = useRef(null);

  const lukeIsDark = !colorIsLight(colorScheme?.slice(-1)[0] || "#000000");
  const isUnloading = useIsUnloading();

  const twoFaPadding = PREMIUM_PADDING;

  const centered = !useIsWiderThan(containerRef, {
    px: loginWidth * 2 - twoFaPadding * 2,
  });

  const luke15OnOneLine = !useIsWiderThan(containerRef, { px: 880 });

  const isTall = useIsTallerThan(containerRef, {
    px: 600,
  });

  const isVeryNarrow = !useIsWiderThan(containerRef, { px: 520 });

  // don't show login form on network signup page
  if (networkShortname === "signup") return null;
  const isFetchedAndNotUnLoading = isFetched && !isUnloading;
  return (
    <FnrFullPageColor colorScheme={colorScheme} ref={containerRef}>
      <PositionDiv
        $centered={centered}
        $alignedTop={!isTall || isVeryNarrow}
        $showShadow={isFetchedAndNotUnLoading}
      >
        <FnrCard
          $backgroundColor={isFetchedAndNotUnLoading ? "grey" : undefined}
          variant={isFetchedAndNotUnLoading ? "modal" : "hidden"}
        >
          <TwoFA
            width={`${loginWidth}px`}
            accentColor={colorScheme?.[0]}
            padding={twoFaPadding}
            showHelpEmail
            stoutInputs
          />
        </FnrCard>
      </PositionDiv>
      <Luke15PositionDiv $onOneLine={luke15OnOneLine}>
        <FnrVirtueSignal
          active={!isFetched || isUnloading || successSignal}
          maxRadius={containerRef.current?.clientWidth / 2}
        />
        <FnrHorizontalGap fullWidth reverse>
          <FnrLuke15
            isDark={lukeIsDark}
            maxWidth={luke15OnOneLine ? "328px" : undefined}
          />
        </FnrHorizontalGap>
      </Luke15PositionDiv>
    </FnrFullPageColor>
  );
};

export default Login;
